import { useCallback, useMemo, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../ui/popover";
import Button from "../../../Button";
import PayGroup from "./PayGroup";
import Date from "./Date";
import { useDateFilters } from "../context/useDateFilters";
import { useCompanyDateFormat } from "../../../../../utils/hooks/company";
import { firstToUpper } from "../../../../../utils/helpers/string";
import { cn } from "../../../../../lib/utils";
import { DateFiltersMode } from "../types";

function Content() {
    const { t } = useTranslation();
    const dateFormat = useCompanyDateFormat();
    const { mode, modes, from, to, payGroupCode, payGroupPeriod, dispatch } = useDateFilters();

    const title: string = useMemo(() => {
        if (mode === "day") {
            return moment(from).format(dateFormat);
        }
        if (mode === "pay-group") {
            if (payGroupCode && payGroupPeriod) {
                return `${payGroupCode} · ${t(payGroupPeriod)}`;
            }
            return t("pay-group");
        }
        return `${moment(from).format(dateFormat)} — ${moment(to).format(dateFormat)}`;
    }, [mode, from, to, dateFormat, payGroupCode, payGroupPeriod, t]);

    const changeRange = useCallback((e: React.MouseEvent, direction: -1 | 1) => {
        e.preventDefault();
        e.stopPropagation();

        let newFrom: string;
        let newTo: string;

        if (mode === "custom") {
            const diff = moment(to).diff(from, "days") + 1;

            newFrom = moment(from)
                .add(direction * diff, "days")
                .format("YYYY-MM-DD");

            newTo = moment(to)
                .add(direction * diff, "days")
                .format("YYYY-MM-DD");
        } else {
            const unit = mode as moment.DurationInputArg2;

            newFrom = moment(from)
                .add(direction, unit)
                .startOf(unit)
                .format("YYYY-MM-DD");

            newTo = moment(from)
                .add(direction, unit)
                .endOf(unit)
                .format("YYYY-MM-DD");
        }

        dispatch({
            type: "set-range",
            payload: { from: newFrom, to: newTo },
        });
    }, [mode, from, to, dispatch]);

    const prev = useCallback((e: React.MouseEvent) => changeRange(e, -1), [changeRange]);
    const next = useCallback((e: MouseEvent) => changeRange(e, 1), [changeRange]);

    const onModeChange = useCallback((value: DateFiltersMode) => {
        dispatch({ type: "set-mode", payload: { mode: value } });
        if (value === "pay-group") {
            dispatch({ type: "set-pay-group-period", payload: { payGroupPeriod: "current" } })
        }
    }, [dispatch])

    const content = useMemo(() => {
        if (mode === "pay-group") {
            return <PayGroup />
        } else {
            return <Date />
        }
    }, [mode]);

    return (
        <Popover>
            <PopoverTrigger asChild>
                <div className="tw-flex tw-items-center tw-font-semibold tw-m-0 tw-border tw-border-l-0 tw-border-color-secondary tw-rounded-r-lg tw-bg-white">
                    {mode !== "pay-group" && (
                        <Button type="button" variant="ghost" onClick={prev}>
                            <ChevronLeft className="tw-size-4 tw-opacity-80" />
                        </Button>
                    )}
                    <div
                        className={cn(
                            "tw-flex tw-items-center tw-gap-2 tw-py-2 tw-text-sm tw-cursor-pointer hover:tw-bg-accent hover:tw-text-accent-foreground tw-px-2",
                            mode === "pay-group" && "tw-rounded-r-lg tw-px-4"
                        )}
                    >
                        <CalendarDays className="tw-size-4 tw-opacity-80" />
                        <span className="tw-min-w-0 tw-truncate tw-text-center">{title}</span>
                    </div>
                    {mode !== "pay-group" && (
                        <Button type="button" variant="ghost" onClick={next}>
                            <ChevronRight className="tw-size-4 tw-opacity-80" />
                        </Button>
                    )}
                </div>
            </PopoverTrigger>

            <PopoverContent
                className="tw-w-[min(100vw-1.5rem,42rem)] tw-max-w-[calc(100vw-1.5rem)] tw-overflow-hidden tw-rounded-lg tw-border-border tw-p-0 tw-shadow-none"
                align="start"
                sideOffset={8}
            >
                <div className="tw-flex tw-flex-col">
                    <div className="tw-py-5">
                        <p className="tw-uppercase tw-text-muted-foreground tw-text-center tw-text-xs tw-font-medium">
                            {t("view-mode")}
                        </p>
                        <div className="tw-border-b tw-border-color-secondary tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2 tw-text-sm">
                            {modes.map((option) => (
                                <span
                                    key={option}
                                    className={cn(
                                        "tw-cursor-pointer tw-rounded-md tw-px-3 tw-py-1.5 tw-font-semibold hover:tw-bg-accent hover:tw-text-accent-foreground",
                                        mode === option ? "tw-bg-accent tw-text-accent-foreground" : "tw-text-muted-foreground"
                                    )}
                                    onClick={() => onModeChange(option)}
                                >
                                    {firstToUpper(t(option))}
                                </span>
                            ))}
                        </div>
                    </div>

                    {content}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export default Content;
